<template>
  <v-menu v-model="menu" location="bottom right">
    <template #activator="{ props: ActivatorProps }">
      <v-btn
        v-show="availableActions.length > 0 || menu"
        v-bind="ActivatorProps"
        aria-label="Participant menu"
        class="signee-menu-activator"
        data-test-id="participant-menu"
        density="compact"
        size="x-small"
        variant="text"
      >
        <v-icon class="text-grey-darken-1"> far fa-ellipsis-vertical </v-icon>
      </v-btn>
    </template>
    <v-card class="menu-card" min-width="195">
      <v-list class="pa-0 ma-0" density="compact">
        <template
          v-for="(item, index) in items"
          :key="`document-menu-item-${index}`"
        >
          <v-list-item
            v-if="item.visible"
            class="pl-2 pr-0 d-flex"
            link
            @click="item.action"
          >
            <v-list-item-title
              class="document-menu-item-title d-flex flex-row align-center justify-start"
              d-flex
              :data-test-id="`${item.dataTestId}`"
              flex-row
            >
              {{ item.title }}
            </v-list-item-title>
          </v-list-item>
        </template>
      </v-list>
    </v-card>
  </v-menu>
</template>
<script setup lang="ts">
import { computed, type PropType, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import type { SignObserverPolicy } from '@/types/deepsign/SignObserverPolicy';
import { useSignContext } from '@/composables/useSignContext';
import { SignStatus } from '@/types/enums/SignStatus';
import { useGlobalStore } from '@/stores/global';
import type { DocumentDialogState } from '@/types/ui';
import { useDocumentContext } from '@/composables/useDocumentContext';
import authService from '@/services/authService.ts';
import type { SigneePolicy } from '@/types/SigneePolicy';
import { useResendInvitations } from '@/composables/useResendInvitations';

const props = defineProps({
  participantId: {
    type: String,
    default: undefined
  },
  participantEmail: {
    type: String,
    default: undefined
  },
  participantPolicy: {
    type: Object as PropType<SigneePolicy | SignObserverPolicy>,
    default: undefined
  },
  participantStatus: {
    type: String as PropType<SignStatus>,
    default: undefined
  },
  participantType: {
    type: String as PropType<'observer' | 'signee' | 'approve'>,
    default: 'signee'
  },
  showActivator: {
    type: Boolean,
    default: false
  }
});

const i18n = useI18n();
const globalStore = useGlobalStore();
const { policy, signeeId } = useSignContext();

const menu = ref(false);

const { currentDocument } = useDocumentContext();
const { requestResendForSignee } = useResendInvitations();

const canForward = computed(() => {
  return (
    authService.isAuthenticated &&
    policy.value.canForward &&
    props.participantId === signeeId.value &&
    (isSignee.value || isApprover.value)
  );
});

const items = computed(() => {
  return [
    {
      title: i18n.t('buttons.edit_email'),
      dataTestId: 'signee-edit',
      visible:
        props.participantPolicy?.canModifyEmail &&
        (isSignee.value || isApprover.value),
      action: () => {
        updateStore('edit');
      }
    },
    {
      title: i18n.t('buttons.remove_approver'),
      dataTestId: 'approver-remove',
      visible:
        props.participantPolicy?.canDelete &&
        isApprover.value &&
        !canForward.value &&
        (currentDocument.value?.signees?.length || 0) > 1,
      action: () => {
        updateStore('remove-approver');
      }
    },
    {
      title: i18n.t('buttons.remove_signee'),
      dataTestId: 'signee-remove',
      visible:
        props.participantPolicy?.canDelete &&
        isSignee.value &&
        !canForward.value &&
        (currentDocument.value?.signees?.length || 0) > 1,
      action: () => {
        updateStore('remove-signee');
      }
    },
    {
      title: i18n.t('buttons.forward_signature'),
      dataTestId: 'signee-forward',
      visible: canForward.value,
      action: () => {
        updateStore('forward');
      }
    },
    {
      title: i18n.t('buttons.send_reminder'),
      dataTestId: 'resend-invitation',
      visible:
        props.participantPolicy?.canResendInvitation &&
        !canForward.value &&
        (isSignee.value || isApprover.value),
      action: () => {
        const documentId = currentDocument.value?.documentId;
        if (!documentId || !props.participantId || !props.participantEmail) {
          return;
        }
        requestResendForSignee({
          documentId,
          signeeId: props.participantId,
          email: props.participantEmail
        });
      }
    },
    {
      title: i18n.t('buttons.remove_observer'),
      dataTestId: 'observer-remove',
      visible: props.participantPolicy?.canDelete && isObserver.value,
      action: () => {
        updateStore('remove-observer');
      }
    }
  ];
});

const availableActions = computed(() => {
  return items.value.filter((item) => item.visible);
});

const isSignee = computed(() => {
  return props.participantType === 'signee';
});

const isApprover = computed(() => {
  return props.participantType === 'approve';
});

const isObserver = computed(() => {
  return props.participantType === 'observer';
});

function updateStore(state: DocumentDialogState) {
  globalStore.documentDialogState = state;

  if (
    props.participantType === 'signee' ||
    props.participantType === 'approve'
  ) {
    console.log(props.participantStatus);
    globalStore.selectedSignee = {
      email: props.participantEmail,
      signeeId: props.participantId,
      status: props.participantStatus
    };
  }

  if (props.participantType === 'observer') {
    globalStore.setSelectedObserver({
      email: props.participantEmail,
      observerId: props.participantId
    });
  }

  globalStore.documentDialog = true;
}
</script>

<style scoped lang="scss">
.signee-menu-activator {
  min-width: 24px !important;
  width: 24px;
  height: 24px;
  padding: 0 !important;
  margin-right: -6px;
}

.menu-card {
  padding: 12px;
  border-radius: 12px;
  border: solid 1px rgb(var(--v-theme-grey-lighten-2));
}

.v-menu__content {
  border-radius: 12px;
}

.theme--light.v-list-item:hover::before {
  opacity: 0;
}

.document-menu-item-title {
  cursor: pointer;
  font-weight: 500;
  font-size: 13px;
  line-height: 18px;
  min-height: 32px;
  justify-content: center;
  align-content: center;

  &:hover {
    color: rgb(var(--v-theme-primary));
  }
}
</style>
